import React from 'react';
import {observable} from 'mobx';
import {observer} from 'mobx-react';

import Canvas from './Canvas.js';
// import CanvasObject from './objects/CanvasObject.js';
import CanvasTimeIndicator from './objects/CanvasTimeIndicator.js';
import ControlPoint from './objects/ControlPoint.js';
import KeyframeObject from './objects/KeyframeObject.js';
import CanvasInput from './objects/CanvasInput.js';

import Bezier from '../../utils/bezier.js';
import CanvasGrid from './CanvasGrid.js';

import './styles.css';

@observer class CurveEdit extends Canvas {

  @observable canAddPoint = false
  @observable canRemovePoint = false
  @observable animationDuration = 1000
  @observable startTime = 0
  @observable currentTime = 0
  @observable keyframes = []
  @observable maxValue = 0;
  @observable minValue = 0;
  @observable property = null;

  constructor(props) {
    super(props);
    this.inverted = false;

    const kObj = this.props.keyframes || {};
    this.keyframes = kObj.keyframes || [];

    this.startTime = this.keyframes[0].time;
    this.animationDuration = this.keyframes[this.keyframes.length - 1].time - this.startTime;
    this.property = this.props.property || '';

    this.color = kObj.color;

    this.findMax();
    this.findMin();

    this.settings = {
      doSnap: true,
      forceSnap: false
    };
    // Finally set default easing to linear
    this.easing = 'linear';
  }

  findMax() {
    const HV = this.keyframes.slice().reduce(
        (min, p) => {
          if(min.highest <= p.value)
            min.highest = p.value;
          return min;
        },
        { highest: 0 }
      ).highest;
    this.maxValue = HV;
  }

  findMin() {
    const MV = this.keyframes.slice().reduce(
        (min, p) => {
          if(min.minest === null || min.minest >= p.value)
            min.minest = p.value;
          return min;
        },
        { minest: null }
      ).minest;
    this.minValue = MV;
  }

  componentWillMount() {
    this.customs = {
      padding: {
        left: 50,
        top: 120,
        right: 50,
        bottom: 100
      }
    }
    super.componentWillMount();
  }

  componentDidMount() {
    super.componentDidMount();

    this.notifyKeyframesUpdate();
  }

  onUpdate() {
    super.onUpdate();
  }

  componentWillUpdate() {
    this.settings = {
      doSnap: this.grid.doSnap,
      forceSnap: this.grid.forceSnap
    };
  }

  componentDidUpdate() {
    super.componentDidUpdate();
  }

  normalizeX(x) {
    return (x - this.customs.padding.left) / this.width;
  }

  normalizeY(y) {
    return (this.height - (y - this.customs.padding.top)) / this.height;
  }

  // Hmhm, was very hard to find out :x
  getEasing() {

    const controls = this.controls;
    const beziers = [];

    for (let i = 0; i < controls.length - 1; i++) {
      let correctIndex = i;
      let nextCorrectIndex = i + 1 >= controls.length
        ? i
        : correctIndex + 1;

      const currentControl = controls[correctIndex];
      const curvePoint = currentControl.curvePoint;

      let controlPoint1 = {};
      if (correctIndex === 0)
        controlPoint1 = currentControl.controlPoints[0];
      else
        controlPoint1 = currentControl.controlPoints[1];

      const nextControl = controls[nextCorrectIndex];
      const nextPoint = nextControl.curvePoint;
      const controlPoint2 = nextControl.controlPoints[0];

      const w = nextPoint.x - curvePoint.x;
      const h = -(
        (nextPoint.y - curvePoint.y) === 0
        ? -0
        : (nextPoint.y - curvePoint.y));

      let p1x = (controlPoint1.x - curvePoint.x) / w;
      let p1y = -(controlPoint1.y - curvePoint.y) / h;

      let p2x = (controlPoint2.x - curvePoint.x) / w;
      let p2y = -(controlPoint2.y - curvePoint.y) / h;

      let timeStart = this.normalizeX(curvePoint.x);
      let timeEnd = this.normalizeX(nextPoint.x);

      const newBezier = Bezier.unitBezier(p1x, p1y, p2x, p2y);

      const newBezierObj = {
        timeStart,
        timeEnd,
        bezier: newBezier,
        startPoint: curvePoint,
        endPoint: nextPoint
      };

      beziers.push(newBezierObj);
    }

    this.easings = beziers.map(el => el.bezier);

    let fn = (time, duration) => {
      time = time < 0 ? 0 : time;
      const _bezier = beziers.find(b => b.timeStart <= time && time <= b.timeEnd);
      const before = beziers[beziers.indexOf(_bezier) - 1];
      const correctTime = (time - (
        before
        ? before.timeEnd
        : 0)) / (_bezier.timeEnd - _bezier.timeStart);

      const y = _bezier.bezier.get(correctTime, duration);
      return {y, startPoint: _bezier.startPoint, endPoint: _bezier.endPoint};
    }

    return fn;

  }

  setEasing(easing) {

    this.saveState(this.easing);

    let unit = Bezier.get(easing);
    this.easing = easing;

    const controls = this.controls;

    const sO = controls[0];
    const eO = controls[controls.length - 1];

    const CP1 = sO.controlPoints[0];
    const CP2 = eO.controlPoints[0];

    const points = unit.points;
    CP1.x = points.p1.x * (this.width) + this.customs.padding.left;
    CP1.y = this.customs.padding.top + this.height - (points.p1.y * this.height);

    CP2.x = points.p2.x * (this.width) + this.customs.padding.left;
    CP2.y = this.customs.padding.top + this.height - (points.p2.y * this.height);

    this.notifyUpdate();
    this.forceUpdate();
  }

  notifyUpdate() {
    super.notifyUpdate();
  }

  notifyCurveUpdate(event) {
    console.log(this.property);
    if(this.props.onCurveUpdate) this.props.onCurveUpdate(event);
  }

  updateGrid(options) {
    this.grid.update(options, this.objects);
    this.notifyUpdate();
  }

  onCanvasClick(e) {
    if (!this.canAddPoint)
      return;

    this.saveState();

    const x = e.clientX - this.customs.padding.left,
      y = e.clientY - this.customs.padding.top;

    if (x < 0 || x > this.width || y < 0 || y > this.height)
      return;

    const time = x / this.width * this.animationDuration;

    const newKF = new KeyframeObject(this, time, x, -60, 8, 8, this.color.value);
    const newPoint = new ControlPoint(this, x, y, newKF);
    newPoint.push();

    const index = this.controls.indexOf(newPoint.control);
    const kf = {
      time,
      value: (1 - (y / this.height)) * (this.maxValue - this.minValue) + this.minValue
    }
    this.keyframes.splice(index, 0, kf);
    this.notifyKeyframesUpdate()
  }

  updateStartAndEnd(Point) {
    const isStartPoint = Point === this.controls[0].curvePoint;

    const secondPoint = isStartPoint ? this.controls[this.controls.length - 1].curvePoint : this.controls[0].curvePoint

    const y = Point.y;
    secondPoint.setY(y, 'updatePos');

  }

  onObjectRemoved(removed) {
    const control = this.controls.find(c => {
      return c.curvePoint.id === removed.id;
    })
    if (control) {
      const index = this.controls.indexOf(control);
      //console.log('indexOf removed: ' + index, "kf: " + this.keyframes[index]);
      const keyframeObj = this.objects.find(obj => obj.x === control.curvePoint.x);
      this.objects.splice(this.objects.indexOf(keyframeObj), 1);
      this.keyframes.splice(index, 1);
      this.controls.splice(index, 1);
    }
    super.onObjectRemoved(removed);
    this.notifyKeyframesUpdate();
  }

  notifyKeyframesUpdate() {
    this.notifyCurveUpdate({
      property: this.property,
      keyframes: this.keyframes
    });
  }

  getRealValue(maxVal, value) {
    return maxVal * value;
  }

  updatePoints() {
    this.findMax();
    this.findMin();

    // sort the keyframes from highest to lowest, and for each KF get the actual control

    const sortedKFs = this.keyframes.slice(0, this.keyframes.length);
    sortedKFs.sort((kf1, kf2) => kf2.value - kf1.value);

    // console.log('last kf:', sortedKFs[sortedKFs.length - 1].value);

    const max = this.maxValue;
    const min = this.minValue;

    // const h = (this.height - min);

    // console.log('height: ', h);

    for(let i = 0; i < sortedKFs.length; i++) {
      const kf = sortedKFs[i];
      const time = kf.time;
      const control = this.controls.find(c => {
        const cTime = (c.curvePoint.x - this.customs.padding.left) / this.width * this.animationDuration;
        return cTime === time;
      });

      const value = (kf.value - min);
      let mult = value / (max - min);
      const newY = (this.height - mult * this.height) + this.customs.padding.top;

      control.curvePoint.setY(newY, 'updatePos');

    }

  }

  loadKeyframes(init) {
    // this.removeObjects(this.keyframeObjs);

    this.findMax();
    this.findMin();

    const last = this.keyframes.length - 1;
    this.animationDuration = this.keyframes[last].time - this.keyframes[0].time;
    this.startTime = this.keyframes[0].time;

    this.controls = [];
    this.objects = [];
    let objs = [];
    let ctls = [];

    for (let i = 0; i < this.keyframes.length; i++) {
      const kf = this.keyframes[i];
      const easing = kf.easing || 'linear';
      const bezier = Bezier.get(easing);

      const points = bezier.points || {};

      const val = this.normalize(kf.value);
      const time = kf.time;
      const x = ((time - this.startTime) / this.animationDuration) * this.width;
      const y = this.height - val * this.height;

      const newKF = new KeyframeObject(this, time, x, -60, 8, 8, this.color.value);

      let newPoint;
      if (i === 0)
        newPoint = new ControlPoint(this, x, y, newKF, {
          controlPoint: {
            x,
            y
          }
        });
      else if (i === this.keyframes.length - 1)
        newPoint = new ControlPoint(this, x, y, newKF, {
          controlPoint: {
            x,
            y
          }
        });
      else
        newPoint = new ControlPoint(this, x, y, newKF);

      let input;

      if(i === 0)
        input = new CanvasInput(this, 5, newPoint.control.curvePoint.y + this.customs.padding.top - 11, 35, 25, {defaultValue: kf.value.toString(), align: 'right', type: 'number'});
      else if(i === this.keyframes.length - 1)
        input = new CanvasInput(this, x + 60, newPoint.control.curvePoint.y + this.customs.padding.top - 11, 35, 25, {defaultValue: kf.value.toString(), align: 'left', type: 'number'});

      if(input) {
        // input.on('validate', e => {
        //   this.keyframes[i].value = parseInt(input.getValue(), 10);
        //   this.findMax();
        //   this.findMin();
        // })

        input.on('validateTyping', e => {
          this.keyframes[i].value = parseFloat(input.getValue());

          const inputVal = parseFloat(input.getValue());

          this.updatePoints();

          if(inputVal > this.keyframes[last].value && i === 0 && !this.inverted)
            this.invertStartAndEnd()
          else if(inputVal > this.keyframes[0].value && i === last && this.inverted)
            this.invertStartAndEnd()
          else if(inputVal < this.keyframes[last].value && i === 0 && this.inverted)
            this.invertStartAndEnd()
          else if(inputVal < this.keyframes[0].value && i === last && !this.inverted)
            this.invertStartAndEnd()
        })
        newPoint.input = input;
      }

      const prevCtl = ctls[i-1];
      if(prevCtl) {
        // Set easing on KF
        {
          let p1x = points.p1.x, p1y = points.p1.y;
          const ctl = prevCtl.controlPoints[prevCtl.controlPoints.length-1];

          let ex = p1x * (x - prevCtl.curvePoint.x) + prevCtl.curvePoint.x;
          let ey = p1y * (y - prevCtl.curvePoint.y) + prevCtl.curvePoint.y;

          ctl.x = ex;
          ctl.y = ey;
        }

        // -----
        {
          let p2x = points.p2.x, p2y = points.p2.y;
          const control = newPoint.control;

          const nowCtl = control.controlPoints[0];

          let ex = p2x * (x - prevCtl.curvePoint.x) + prevCtl.curvePoint.x
          let ey = (p2y * y);

          nowCtl.x = ex;
          nowCtl.y = ey;
        }

      }

      objs.push(...newPoint.points, newKF);
      if(newPoint.input) objs.push(newPoint.input);
      ctls.push(newPoint.control);
    }
    if(init) {
      this.objects.push(...objs);
      this.controls.push(...ctls);
    } else {
      // this.objects.push(...objs);
      this.controls.push(...ctls);
      this.addObject(...objs);
    }

    this.initTI(init);

    const event = {
      property: this.property,
      keyframes: this.keyframes
    };

    this.notifyCurveUpdate(event);
  }

  normalize(value) { return (value - this.minValue) / (this.maxValue - this.minValue); }

  initTI(init) {
    this.TIObj = new CanvasTimeIndicator(0, -65, 10, 10, this);
    this.TIObj.on('move', (e) => {
      // const x = (this.currentTime / this.animationDuration) * this.width + this.customs.padding.left;
      const time = (e.to.x - this.customs.padding.left) / this.width * this.animationDuration;
      // console.log(time);
      this.currentTime = time;
      this.preview()
    })
    if(init)
      this.objects.push(this.TIObj);
    else this.addObject(this.TIObj);
  }

  onInit() {
    // Setup the grid
    this.grid = new CanvasGrid(this.width, this.height + 100, 10, 12, {
      context: this.getContext(),
      x: this.customs.padding.left,
      y: this.customs.padding.top - 50,
      draw: {
        stroke: '#2E3440 2'
      }
    });

    // Assuming the only one is cubicBezier

    // Init controls
    this.controls = [];

    // Init TimeIndicator for canvas
    this.initTI(true);

    // const objs = [];
    // const ctls = [];

    this.loadKeyframes(true);

    // this.objects.push(this.TIObj);
    // this.controls.push();

    if (this.settings) {
      let doSnap = this.settings.doSnap;
      let forceSnap = this.settings.forceSnap;

      this.updateGrid({
        doSnap,
        snapFree: !forceSnap
      });

      this.refs['c_snapToGrid'].checked = doSnap;
      this.refs['c_snapToGrid'].enable = !forceSnap;
      this.refs['c_forceSnap'].checked = forceSnap;
    }

    this.background(this.props.background || '#252A33');

  }

  invertStartAndEnd() {
    this.inverted = !this.inverted
    const startPoint = this.controls[0].curvePoint;
    const endPoint = this.controls[this.controls.length - 1].curvePoint;

    const prevY = JSON.parse(JSON.stringify(endPoint.y));

    // endPoint.y = startPoint.y;
    // startPoint.y = prevY;
    endPoint.setY(startPoint.y, 'updatePos');
    startPoint.setY(prevY, 'updatePos');

  }

  onHistoryRestored(objects) {
    const controls = objects[1];
    if (controls) {
      this.controls = controls;
    }
    const easing = objects[2];
    if (easing) {
      this.easing = easing;
    }

    this.forceUpdate();
  }

  onObjectStartMove(object) {
    this.saveState();
    // Update display
    if (this.easing !== 'custom') {
      this.easing = 'custom';
      this.forceUpdate();
    }
  }

  saveState(...toSave) {
    this.history.saveState([
      this.copyArray(this.objects),
      this.copyArray(this.controls),
      ...toSave
    ]);
  }

  undo() {
    this.history.undo();
  }

  drawTimeIntervals() {
    const context = this.getContext();
    const y = this.grid.y - 5;

    const duration = this.animationDuration;
    const startTime = this.startTime;

    const unit = startTime + duration < 1000
      ? "ms"
      : "s";
    const divby = (
      unit === "s"
      ? 1000
      : 1);

    const st = startTime / divby;
    const et = (startTime + duration) / divby;

    context.save();

    context.globalCompositionOperation = 'destination-over';

    context.font = '14px Microsoft Yi Baiti'
    context.strokeStyle = '#2E3440';
    context.fillStyle = 'white';
    context.lineWidth = 2;
    context.textAlign = "center";

    // First time interval

    context.beginPath();
    context.moveTo(this.xCoord(0), y);
    context.lineTo(this.xCoord(0), y - 16);
    context.stroke();

    context.fillText(st + unit, this.xCoord(0), y - 20)

    // Last time interval

    context.beginPath();
    context.moveTo(this.xCoord(this.width), y);
    context.lineTo(this.xCoord(this.width), y - 16);
    context.stroke();

    context.fillText(et + unit, this.xCoord(this.width), y - 20)

    // In between
    // const remain = Math.round((Math.round(duration / divby) - (duration / divby)) * 100) / 100;
    // console.log(remain);

    // context.lineWidth = 3;
    if (unit === 's') {

      const interval = this.width / ((duration / 1000) * 10);

      context.beginPath();
      for (let i = 0; i < Math.round(this.width / interval); i++) {
        const x = i * interval;
        let my = 8;
        if (i % 5 === 0)
          my = 12;
        if (i % 10 === 0)
          my = 16;
        context.moveTo(this.xCoord(x), y);
        context.lineTo(this.xCoord(x), y - my);
      }
      context.stroke();

    } else {
      const interval = this.width / ((duration / 100));

      context.beginPath();
      for (let i = 0; i < Math.round(this.width / interval); i++) {
        const x = i * interval;
        let my = 8;
        if (i % 5 === 0)
          my = 12;
        if (i % 10 === 0)
          my = 16;
        context.moveTo(this.xCoord(x), y);
        context.lineTo(this.xCoord(x), y - my);
      }
      context.stroke();
    }

    context.restore();
  }

  updateTimeIndicator(max) {
    if (this.TIObj)
      this.TIObj.x = (
        max
        ? 1
        : this.currentTime / this.animationDuration) * this.width + this.customs.padding.left;
  }

  drawProperty() {
    const context = this.getContext();

    context.save();

    context.globalCompositeOperation = 'source-over'
    context.fillStyle = this.color.value;
    context.beginPath();
    context.translate(10, 0);
    context.rotate(Math.PI / 4);
    context.fillRect(10 / 2, -10 / 2, 10, 10);
    context.restore();

    context.save();

    context.font = '20px Microsoft Yi Baiti'
    context.fillStyle = 'white';
    context.textAlign = "left";

    context.fillText(this.property, 30, 12);

    context.restore();

  }

  onDraw() {
    this.clear();
    const context = this.getContext();
    const controls = this.controls;

    const sO = controls[0];
    const eO = controls[controls.length - 1];

    const SP = sO.curvePoint;
    const EP = eO.curvePoint;

    const CP1 = sO.controlPoints[0];
    const CP2 = eO.controlPoints[0];

    // display the grid
    this.grid.draw();

    // display the time intervals
    this.drawTimeIntervals();

    // display the property
    this.drawProperty();

    // display the curve

    context.save();

    context.lineCap = 'round';
    context.strokeStyle = 'white';

    context.lineWidth = 0.5;
    context.setLineDash([8, 2]);

    // If more than 2 points lol
    if (controls.length > 2) {

      for (let i = 0; i < controls.length; i++) {
        const currentControl = controls[i];
        const curvePoint = currentControl.curvePoint;
        const CP1 = currentControl.controlPoints[0];
        if (this.isHidden(CP1))
          continue;
        const CP2 = currentControl.controlPoints[1];

        context.beginPath();
        context.moveTo(CP1.x, CP1.y);
        context.lineTo(curvePoint.x, curvePoint.y);
        if (CP2)
          context.lineTo(CP2.x, CP2.y);
        context.stroke();
      }

    } else {

      if (!this.isHidden(CP1)) {
        context.beginPath();
        context.moveTo(SP.x, SP.y);
        context.lineTo(CP1.x, CP1.y);
        context.stroke();
      }

      if (!this.isHidden(CP2)) {
        context.beginPath();
        context.moveTo(EP.x, EP.y);
        context.lineTo(CP2.x, CP2.y);
        context.stroke();
      }

    }

    context.lineWidth = 1;
    context.setLineDash([]);

    // If more than 2 points lol
    if (controls.length > 2) {
      context.beginPath();
      for (let i = 0; i < controls.length - 1; i++) {

        let correctIndex = i;
        let nextCorrectIndex = i + 1 >= controls.length
          ? i
          : correctIndex + 1;

        const currentControl = controls[correctIndex];
        const curvePoint = currentControl.curvePoint;

        let controlPoint1 = {};
        if (correctIndex === 0)
          controlPoint1 = currentControl.controlPoints[0];
        else
          controlPoint1 = currentControl.controlPoints[1];

        if(i > 0 && i < controls.length - 1) {
          // if focused, display the value beside
          if(currentControl.curvePoint.focused) {
            context.font = '16px Microsoft Yi Baiti'
            context.fillStyle = 'white';
            context.textAlign = "left";
            context.fillText(Math.round(this.keyframes[i].value).toString(), curvePoint.x + 10, curvePoint.y - 10);
          }
        }

        const nextControl = controls[nextCorrectIndex];
        const nextPoint = nextControl.curvePoint;
        const controlPoint2 = nextControl.controlPoints[0];

        context.moveTo(curvePoint.x, curvePoint.y);
        context.bezierCurveTo(controlPoint1.x, controlPoint1.y, controlPoint2.x, controlPoint2.y, nextPoint.x, nextPoint.y);
      }
      context.stroke();
    } else {
      context.beginPath();
      context.moveTo(SP.x, SP.y);
      context.bezierCurveTo(CP1.x, CP1.y, CP2.x, CP2.y, EP.x, EP.y);
      context.stroke();
    }

    context.restore();
    super.onDraw();

  }

  handleCheckClick(type) {
    let checkbox = this.refs['c_' + type];
    if (checkbox) {
      switch (type) {
        case 'snapToGrid':
          {
            this.updateGrid({doSnap: checkbox.checked});
            break;
          }

        case 'forceSnap':
          {
            let otherC = this.refs['c_snapToGrid'];
            let doSnap;
            if (checkbox.checked) { //Force snap
              doSnap = true;
              otherC.checked = true;
              otherC.disabled = true;
            } else { // Dont force snap
              doSnap = otherC.checked;
              otherC.disabled = false;
            }
            this.updateGrid({
              snapFree: !checkbox.checked,
              doSnap
            });
            break;
          }

        default:
          break;
      }
    }
  }

  drawTimeIndicatorOnCurve(value) {
    // this.onDraw();
    const context = this.getContext();
    const time = this.currentTime;

    const x = (time / this.animationDuration) * this.width + this.customs.padding.left;

    context.save();

    context.fillStyle = 'white';
    context.strokeStyle = '#252A33';
    context.lineWidth = 3;

    context.beginPath();
    context.arc(x, value, 5, 0, Math.PI * 2, false);
    context.closePath();
    context.fill();
    context.stroke();

    context.strokeStyle = 'white';
    context.lineWidth = 0.1;

    context.beginPath();
    context.moveTo(x, value);
    context.lineTo(this.width + this.customs.padding.left, value);
    context.stroke();

    context.restore();

  }

  drawPreviewArrow(value) {
    const context = this.getContext();
    // const value = this.getValue().top;
    const x = this.width + this.customs.padding.left + 15;

    context.save();

    context.fillStyle = 'white';
    context.strokeStyle = '#252A33';
    context.lineWidth = 4;

    context.beginPath();
    context.moveTo(x, value);
    context.lineTo(x + 5, value - 5);
    context.lineTo(x + 5, value + 5);
    context.closePath();
    context.fill();

    context.beginPath();
    context.fillRect(x + 5, value - 5, 15, 10)

    context.restore();

  }

  previewTillEnd() {
    if (this.runLoop)
      clearInterval(this.runLoop);

    if (this.aTimeout)
      clearTimeout(this.aTimeout);

    if (this.updateStateLoop)
      clearInterval(this.updateStateLoop);

    const FPS = 120;
    let time = this.currentTime;

    this.runLoop = setInterval(() => {

      if (time > this.animationDuration) {
        time = this.animationDuration;
        this.updateTimeIndicator(true);
        clearInterval(this.runLoop);
        this.runLoop = null;
        this.aTimeout = setTimeout(() => {
          this.currentTime = 0
          this.updateTimeIndicator();
          this.onDraw()
        }, 1000);
      }

      this.currentTime = time;
      const {top, bezierValue} = this.getValue();

      this.drawTimeIndicatorOnCurve(top);
      this.drawPreviewArrow(top);
      this.updateTimeIndicator();

      // const bezierValue = (kfValue - this.minValue) / (this.maxValue - this.minValue);

      const kfValue = (bezierValue * (this.maxValue - this.minValue)) + this.minValue;

      const event = {
        name: 'preview',
        data: {
          value: kfValue
        },
        property: this.property,
        keyframes: this.keyframes
      }

      this.notifyCurveUpdate(event);

      time += 1000 / FPS;

    }, 1000 / FPS)
  }

  getValue(time) {
    if(!time)
      time = this.currentTime;
    const easing = this.getEasing();

    let e = easing(time / this.animationDuration, this.animationDuration);
    let y = e.y || 0;
    let sp = e.startPoint;
    let ep = e.endPoint;

    let value = y * (-(ep.y - sp.y)) + (this.height - sp.y + this.customs.padding.top);

    let bezierValue = value / this.height;

    // console.log(bezierValue, y, this.height);

    let top = (this.height + this.customs.padding.top) - value;
    return {value, top, bezierValue};
  }

  preview() {
    const {top, bezierValue} = this.getValue();

    // this.drawTimeIndicatorOnCurve(top);
    this.drawPreviewArrow(top);
    this.updateTimeIndicator();

    // console.log(bezierValue);

    const kfValue = (bezierValue * (this.maxValue - this.minValue)) + this.minValue;

    const event = {
      name: 'preview',
      data: {
        value: kfValue
      },
      property: this.property,
      keyframes: this.keyframes
    }

    this.notifyCurveUpdate(event);

  }

  updateKeyframe(prevTime, time) {
    //console.log('updating KF time from ', prevTime, 'to', time);
    const kfbytime = this.keyframes.find(kf => kf.time === prevTime);
    if(kfbytime) {
      kfbytime.time = time;
    }

    this.updateKeyframes();
  }

  ease(easing) {
    if(!easing.points) return 'linear';
    const points = easing.points;
    if(points.p1.x === 0 && points.p1.y === 0 && points.p2.x === 1 && points.p2.y === 1) return 'linear';
    else {
      return 'cubic-bezier(' + points.p1.x + ', ' + points.p1.y + ', ' + points.p2.x + ', ' + points.p2.y + ')';
    }
  }

  getKeyframes() {
    return this.keyframes;
  }

  updateKeyframes(newKF) {
    if(newKF) {
      this.keyframes = newKF.keyframes;
      this.color = newKF.color;
      this.notifyUpdate();
      return;
    }
    for(let i = 0; i < this.keyframes.length; i++) {
      const kf = this.keyframes[i];
      const time = kf.time;
      const val = this.getKFValueB(time);

      if(this.easings) {
        const index = i;

        const easing = this.easings[index - 1];

        if(easing) {
          kf.easing = this.ease(easing);
          // console.log('len:', this.easings.length, 'time:', time, 'easing:', kf.easing);
        }
      }

      kf.value = val;
    };

  }

  getKFValueB(time) {
    const bezierValue = this.getValue(time).bezierValue;
    const KFValue = (bezierValue * (this.maxValue - this.minValue)) + this.minValue;
    return KFValue;
  }


  getKFValue(time) {
    return this.keyframes.find(kf => kf.time === time).value;
  }

  generateSVGForEasing(easing, width, height, stroke) {
    let padding = 5;
    let unit = Bezier.get(easing);
    let points = unit.points;
    let p1 = points.p1,
      p2 = points.p2;

    let CP1 = (p1.x * width + padding) + "," + (
    height - (p1.y * height) - padding);
    let CP2 = (p2.x * width - padding) + "," + (
    height - (p2.y * height) + padding);

    const path = "M" + padding + "," + (
    height - padding) + " C" + CP1 + " " + CP2 + " " + (
    width - padding) + "," + (
    padding);

    return (<svg className={this.easing === easing
        ? "active"
        : ""} onClick={() => this.setEasing(easing)} width={width} height={height}>
      <path d={path} fill="none" stroke={stroke || 'black'} strokeWidth={1}/>
    </svg>)
  }

  toggleAddPoints() {
    this.canAddPoint = !this.canAddPoint
  }

  toggleRemovePoints() {
    this.canRemovePoint = !this.canRemovePoint
  }

  handleChange(e) {
    const value = e.target.value;
    this.animationDuration = value;
  }

  exportKeyframes() {
    const keyframes = this.getKeyframes();
    console.log(JSON.stringify(keyframes));
  }

  loadExternalKeyframes() {
    const data = prompt("Please past keyframe data here");
    if(data)
      this.loadKeyframesFromData(data);
  }

  loadKeyframesFromData(data) {
    let keyframeData = data;
    const keyframes = JSON.parse(keyframeData);

    // this.updateKeyframes(keyframes);
    this.keyframes = keyframes;
    this.loadKeyframes(false);
  }

  setProperty(property) {
    this.controls.forEach(control => control.curvePoint.focused = false);
    this.property = property;
  }

  onRender() {
    const easings = this.keyframes.length === 2 ? (<div className="easings">
      {this.generateSVGForEasing('linear', 40, 40, 'white')}
      {this.generateSVGForEasing('ease', 40, 40, 'white')}
      {this.generateSVGForEasing('easeIn', 40, 40, 'white')}
      {this.generateSVGForEasing('easeOut', 40, 40, 'white')}
      {this.generateSVGForEasing('easeInOut', 40, 40, 'white')}
    </div>) : null;
    return (<div>
      {easings}
      <div className="settings">
        <input ref="c_snapToGrid" type="checkbox" onClick={() => this.handleCheckClick('snapToGrid')}/>
        <label>Snap to grid</label>
        <input ref="c_forceSnap" type="checkbox" onClick={() => this.handleCheckClick('forceSnap')}/>
        <label>Force Snap</label>
      </div>
      <div className="preview">
        <button onClick={() => this.previewTillEnd()}>Preview</button>
      </div>
      <button onClick={() => this.toggleAddPoints()}>{
          this.canAddPoint
            ? "Stop adding points"
            : "Add points"
        }</button>
      <button onClick={() => this.toggleRemovePoints()}>{
          this.canRemovePoint
            ? "Back to normal"
            : "Remove points"
        }</button>
      <button onClick={() => this.undo()}>Undo</button>
      <br/>
      {/*<label>Animation duration</label>
      <input onChange={this.handleChange.bind(this)} type="number" value={this.animationDuration}/>*/}
      <span>{Math.round(this.currentTime / 1000 * 1000) / 1000}</span>
      <br />
      <button onClick={this.exportKeyframes.bind(this)}>Export keyframes</button>
      <button onClick={this.loadExternalKeyframes.bind(this)}>Load keyframes</button>
    </div>)
  }

}

export default CurveEdit;



// WEBPACK FOOTER //
// ./src/components/canvas/CurveEdit.js